import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  type LayerFeature,
  useFacets,
  useLayer,
  useOsmLayer,
  usePatchSite,
  useSiteIds,
} from "../lib/api";
import {
  DEFAULTS,
  filterPart,
  type Search,
  type SearchPatch,
  withTierDefault,
} from "../lib/search";
import { usePersisted } from "../lib/theme";
import { AddToListDialog } from "../ui/AddToListDialog";
import { FilterRail } from "../ui/FilterRail";
import { Legend } from "../ui/Legend";
import {
  type Camera,
  type ColorBy,
  MapCanvas,
  type PinState,
  type ViewState,
} from "../ui/MapCanvas";
import { ResultsStrip } from "../ui/ResultsStrip";
import { SitePanel, TABS, type TabId } from "../ui/site/SitePanel";
import { useToast } from "../ui/Toast";

const route = getRouteApi("/");

export function MapPage() {
  const search = route.useSearch();
  const navigate = useNavigate({ from: "/" });
  const toast = useToast();

  const patch = useCallback(
    (p: SearchPatch, replace = false) => {
      void navigate({ search: (prev) => ({ ...prev, ...p }), replace });
    },
    [navigate],
  );
  const reset = useCallback(() => {
    void navigate({
      search: (prev: Search) => ({
        mw_min: DEFAULTS.mw_min,
        mw_max: DEFAULTS.mw_max,
        page: DEFAULTS.page,
        per_page: DEFAULTS.per_page,
        site: prev.site,
        tab: prev.tab,
        sat: prev.sat,
        basemap: prev.basemap,
        osm: prev.osm,
        c: prev.c,
        z: prev.z,
      }),
    });
  }, [navigate]);

  // Data: static layer + server-side id set for the active filters (docs/questions.md Q14).
  const layer = useLayer();
  const facets = useFacets();
  const hasScores = facets.data?.has_scores ?? false;
  const filters = useMemo(
    () => withTierDefault(filterPart(search), hasScores),
    [search, hasScores],
  );
  const ids = useSiteIds(filters);
  const idMap = useMemo(() => {
    const m = new Map<number, { units: number; mw: number; mwh: number | null }>();
    for (const [id, units, mw, mwh] of ids.data?.items ?? []) m.set(id, { units, mw, mwh });
    return m;
  }, [ids.data]);
  const features = useMemo<LayerFeature[]>(() => {
    if (!layer.data) return [];
    if (!ids.data) return [];
    return layer.data.features.filter((f) => idMap.has(f.properties.id));
  }, [layer.data, ids.data, idMap]);

  // Viewport aggregates for the results strip.
  const [view, setView] = useState<ViewState | null>(null);
  const inView = useMemo(() => {
    const agg = { sites: 0, units: 0, mw: 0, mwh: null as number | null, ids: [] as number[] };
    if (!view) return agg;
    const [w, s, e, n] = view.bounds;
    const visibleIds: number[] = [];
    for (const f of features) {
      const [lon, lat] = f.geometry.coordinates;
      if (lon < w || lon > e || lat < s || lat > n) continue;
      const v = idMap.get(f.properties.id);
      if (!v) continue;
      agg.sites++;
      agg.units += v.units;
      agg.mw += v.mw;
      if (v.mwh !== null) agg.mwh = (agg.mwh ?? 0) + v.mwh;
      visibleIds.push(f.properties.id);
    }
    agg.ids = visibleIds;
    return agg;
  }, [features, idMap, view]);

  // Camera to URL, debounced.
  const camTimer = useRef<number | null>(null);
  const onViewChange = useCallback(
    (v: ViewState) => {
      setView(v);
      if (camTimer.current) window.clearTimeout(camTimer.current);
      camTimer.current = window.setTimeout(() => patch({ c: v.center, z: v.zoom }, true), 500);
    },
    [patch],
  );

  // OSM outlines load lazily on first deep zoom.
  const [wantOsm, setWantOsm] = useState(false);
  const osm = useOsmLayer(wantOsm && search.osm !== false);
  const onNeedOsm = useCallback(() => setWantOsm(true), []);

  // Drawer + fly-to when a site is opened without a camera in the URL.
  const selectedId = search.site;
  const [fly, setFly] = useState<(Camera & { key: number }) | undefined>(undefined);
  const flownFor = useRef<number | null>(null);
  useEffect(() => {
    if (!selectedId || !layer.data || flownFor.current === selectedId) return;
    const f = layer.data.features.find((x) => x.properties.id === selectedId);
    if (!f) return;
    flownFor.current = selectedId;
    if (search.c === undefined) {
      setFly({ center: f.geometry.coordinates, zoom: Math.max(search.z ?? 0, 9), key: Date.now() });
    }
  }, [selectedId, layer.data, search.c, search.z]);

  const onSelect = useCallback((id: number) => patch({ site: id, tab: undefined }), [patch]);
  const closeDrawer = useCallback(() => patch({ site: undefined, tab: undefined }), [patch]);
  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !(e.target instanceof HTMLInputElement)) closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, closeDrawer]);

  // Pin verification (PRD §12.3): satellite on, draggable marker, PATCH on save.
  const [pin, setPin] = useState<PinState>(null);
  const patchSite = usePatchSite();
  const verify = useMemo(
    () => ({
      pin,
      saving: patchSite.isPending,
      start: (siteId: number, lat: number, lon: number) => {
        setPin({ siteId, lat, lon });
        if (!search.sat) patch({ sat: true }, true);
      },
      cancel: () => setPin(null),
      save: async () => {
        if (!pin) return;
        try {
          await patchSite.mutateAsync({
            siteId: pin.siteId,
            patch: { coordinates: { lat: pin.lat, lon: pin.lon, precision_m: 50 } },
          });
          toast("Coordinates saved (coord_source = manual, ±50 m)");
          setPin(null);
        } catch (e) {
          toast(e instanceof ApiError ? e.message : "Save failed", "danger");
        }
      },
    }),
    [pin, patchSite, patch, search.sat, toast],
  );
  const onPinMove = useCallback(
    (lat: number, lon: number) => setPin((p) => (p ? { ...p, lat, lon } : p)),
    [],
  );

  // Add to list.
  const [dialogIds, setDialogIds] = useState<number[] | null>(null);
  const [colorBy, setColorBy] = usePersisted<ColorBy>("map:colorBy", "tier");

  const tab = (TABS.some((t) => t.id === search.tab) ? search.tab : "overview") as TabId;
  const initialCamera = useRef<Camera | undefined>(
    search.c ? { center: search.c, zoom: search.z ?? 6 } : undefined,
  ).current;

  const total = ids.data
    ? { sites: ids.data.total, units: ids.data.units, mw: ids.data.mw, mwh: ids.data.mwh }
    : undefined;

  return (
    <div className="flex h-full min-h-0">
      <FilterRail search={search} patch={patch} reset={reset} />
      <div className="relative min-w-0 flex-1">
        <MapCanvas
          features={features}
          osm={osm.data}
          basemap={search.basemap ?? "positron"}
          satellite={Boolean(search.sat)}
          showOsm={search.osm !== false}
          colorBy={colorBy}
          selectedId={selectedId}
          initialCamera={initialCamera}
          flyTo={fly}
          pin={pin}
          onPinMove={onPinMove}
          onViewChange={onViewChange}
          onSelect={onSelect}
          onNeedOsm={onNeedOsm}
        />
        <div className="pointer-events-none absolute top-3 left-3 right-14 flex flex-col gap-2">
          <ResultsStrip
            inView={inView}
            total={total}
            loading={ids.isLoading || layer.isLoading}
            onAddAll={() => setDialogIds(inView.ids)}
            colorBy={colorBy}
            onColorBy={setColorBy}
          />
        </div>
        <div className="pointer-events-none absolute bottom-7 left-3 flex flex-col gap-1">
          <div className="pointer-events-auto">
            <Legend colorBy={colorBy} />
          </div>
        </div>
        {layer.data && layer.data.features.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="dialog pointer-events-auto text-center">
              <p>No published layer. Run the pipeline: publish_layer, then reload.</p>
            </div>
          </div>
        ) : null}
        {ids.error ? (
          <div
            className="pointer-events-auto absolute top-16 left-3 rounded border px-3 py-2 text-[12px]"
            style={{ borderColor: "var(--danger)", background: "var(--panel)" }}
          >
            {ids.error instanceof ApiError ? ids.error.message : "Filter query failed"}
          </div>
        ) : null}
      </div>
      {selectedId ? (
        <aside
          className="flex w-[540px] shrink-0 flex-col border-l"
          style={{ borderColor: "var(--line)", background: "var(--panel)" }}
          aria-label="Site drawer"
          data-testid="site-drawer"
        >
          <SitePanel
            siteId={selectedId}
            tab={tab}
            onTab={(t) => patch({ tab: t === "overview" ? undefined : t }, true)}
            onClose={closeDrawer}
            verify={verify}
            variant="drawer"
            onAddToList={(id) => setDialogIds([id])}
          />
        </aside>
      ) : null}
      <AddToListDialog
        open={dialogIds !== null}
        siteIds={dialogIds ?? []}
        onClose={() => setDialogIds(null)}
        onDone={(_id, name) =>
          toast(
            `Added ${dialogIds?.length ?? 0} site${(dialogIds?.length ?? 0) === 1 ? "" : "s"} to ${name}`,
          )
        }
      />
    </div>
  );
}
